/**
 * The frame the cell pop-ups (assign an officer, assign/erase a counter) sit in, by screen size:
 *   < 768px  a bottom sheet, full width and thumb-reachable (phone)
 *   ≥ 768px  a panel floating beside the cell it was opened from (iPad / desktop), which the user can drag
 *            anywhere by its grip bar when it covers part of the grid they need to see. It opens beside the
 *            cell each time; a double-click on the grip puts it back there.
 * A tap outside either one cancels.
 */
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { UI_SCALE } from '@/utils/uiScale';
import { useDragOffset } from '@/utils/useDragOffset';
import { clampToViewport, isNarrow } from '@/utils/popoverLayout';

const MARGIN = 12;

export function PopoverShell({ anchorX, anchorY, width, approxHeight, onCancel, children }: {
  /** Where it was opened from (viewport px). */
  anchorX: number;
  anchorY: number;
  /** Panel width in unscaled px (it is zoomed by UI_SCALE like the rest of the grid's pop-ups). */
  width: number;
  /** Height used to place it before it has been measured. */
  approxHeight: number;
  onCancel: () => void;
  children: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(approxHeight * UI_SCALE);
  const { offset, dragging, handleProps } = useDragOffset();
  // Measured, so it is kept on screen by its real height (the content changes height as pickers open).
  useLayoutEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const measure = () => setHeight(el.getBoundingClientRect().height);
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, []);

  if (isNarrow()) {
    return (
      <>
        <div onClick={onCancel} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1900 }} />
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed', bottom: 0, left: 0, right: 0, background: 'white',
            borderRadius: '16px 16px 0 0', padding: '20px 20px 32px', // extra bottom padding for the home indicator
            zIndex: 1901, boxShadow: '0 -4px 24px rgba(0,0,0,0.15)',
          }}
        >
          <div style={{ width: 40, height: 4, borderRadius: 2, background: '#d1d5db', margin: '0 auto 16px' }} />
          {children}
        </div>
      </>
    );
  }

  // Beside the cell — to its right, or its left when there is no room — then moved by however far it was dragged,
  // and kept on screen.
  const w = width * UI_SCALE;
  const beside = {
    left: anchorX + 18 + w > window.innerWidth - MARGIN ? anchorX - w - 8 : anchorX + 18,
    top: anchorY - height / 2,
  };
  const { left, top } = clampToViewport(
    { left: beside.left + offset.dx, top: beside.top + offset.dy },
    { width: w, height },
    { width: window.innerWidth, height: window.innerHeight },
    MARGIN,
  );

  return (
    <>
      <div onClick={onCancel} style={{ position: 'fixed', inset: 0, zIndex: 1900 }} />
      <div
        ref={panelRef}
        onClick={(e) => e.stopPropagation()}
        data-testid="popover-panel"
        style={{
          position: 'fixed', left: left / UI_SCALE, top: top / UI_SCALE, // zoom scales offsets too
          zoom: UI_SCALE, width, background: 'white', borderRadius: 12, overflow: 'hidden',
          boxShadow: dragging
            ? '0 14px 40px rgba(0,0,0,0.25), 0 2px 8px rgba(0,0,0,0.1)'
            : '0 8px 32px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid #e2e8f0', zIndex: 1901,
        }}
      >
        <div
          {...handleProps}
          data-testid="popover-drag-handle"
          title="Drag to move · double-click to put it back"
          style={{
            ...handleProps.style, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            height: 18, background: dragging ? '#e2e8f0' : '#f1f5f9', borderBottom: '1px solid #e2e8f0',
            color: '#64748b', fontSize: 10, fontWeight: 600, letterSpacing: '0.04em',
          }}
        >
          <span aria-hidden style={{ fontSize: 12, lineHeight: 1, letterSpacing: '-1px' }}>⠿⠿</span>
          DRAG TO MOVE
        </div>
        <div style={{ padding: '12px 18px 16px' }}>{children}</div>
      </div>
    </>
  );
}
